/**
 * Socket Adapters - Index & Factory
 *
 * Exports all adapter types and provides factory function
 * for creating the appropriate adapter based on mode.
 *
 * Part of Phase 2: Shared Interface & Adapter Pattern
 */

// Re-export types
export type {
  ISocketAdapter,
  SocketMode,
  AdapterCapabilities,
  ConnectionConfig,
  ConnectionState,
  SessionOptions,
  NormalizedSession,
  NormalizedMessage,
  StreamChunk,
  SocketEvents,
  PairingCode,
  AuthTokens,
  DaemonStatus,
  DaemonInfo,
  ContextInfo,
  MessageRole,
  SessionStatus,
  AuthPayload,
  SocketOptions,
  SessionStateEvent,
  SyncedSession,
} from '../types/socketAdapter';

// Export adapters
export { BaseSocketAdapter } from './BaseSocketAdapter';
export { SelfHostedSocketAdapter } from './SelfHostedSocketAdapter';

// Import for factory
import type { ISocketAdapter, SocketMode, ConnectionConfig } from '../types/socketAdapter';
import { SelfHostedSocketAdapter } from './SelfHostedSocketAdapter';
import { getPreferredMode } from '../config/featureFlags';

// ============================================
// AdapterManager Class (P3-T2: Lifecycle Management)
// ============================================

/**
 * AdapterManager - Centralized lifecycle management for socket adapters
 *
 * Provides:
 * - Lazy adapter creation via getAdapter()
 * - Proper cleanup via dispose() and reset()
 * - HMR support for development
 * - Singleton pattern per mode
 *
 * Part of Phase 3: Hook Extraction Patterns
 */
export class AdapterManager {
  private adapters: Map<SocketMode, ISocketAdapter> = new Map();
  private disposed: boolean = false;

  /**
   * Get or create an adapter for the specified mode
   * Uses lazy initialization - adapters are only created when first requested
   *
   * @param mode - Socket mode (only 'self-hosted' is supported)
   * @returns The adapter instance for that mode
   */
  getAdapter(mode: SocketMode): ISocketAdapter {
    if (this.disposed) {
      console.warn('[AdapterManager] Manager is disposed, creating new adapter');
      this.disposed = false;
    }

    let adapter = this.adapters.get(mode);
    if (!adapter) {
      console.log(`[AdapterManager] Creating new ${mode} adapter`);
      adapter = new SelfHostedSocketAdapter();
      this.adapters.set(mode, adapter);
    }
    return adapter;
  }

  /**
   * Get the currently connected adapter (if any)
   *
   * @returns Connected adapter or null
   */
  getConnectedAdapter(): ISocketAdapter | null {
    for (const adapter of this.adapters.values()) {
      if (adapter.isConnected()) {
        return adapter;
      }
    }
    return null;
  }

  /**
   * Get the mode of the currently connected adapter
   *
   * @returns Connected mode or null
   */
  getConnectedMode(): SocketMode | null {
    for (const [mode, adapter] of this.adapters.entries()) {
      if (adapter.isConnected()) {
        return mode;
      }
    }
    return null;
  }

  /**
   * Check if any adapter is currently connected
   */
  isAnyConnected(): boolean {
    for (const adapter of this.adapters.values()) {
      if (adapter.isConnected()) {
        return true;
      }
    }
    return false;
  }

  /**
   * Disconnect and destroy all adapters
   * Call this during cleanup (e.g., HMR, page unload)
   */
  dispose(): void {
    console.log('[AdapterManager] Disposing all adapters...');

    for (const [mode, adapter] of this.adapters.entries()) {
      try {
        if (adapter.isConnected()) {
          console.log(`[AdapterManager] Disconnecting ${mode} adapter`);
          adapter.disconnect();
        }
      } catch (error) {
        console.error(`[AdapterManager] Error disposing ${mode} adapter:`, error);
      }
    }

    this.adapters.clear();
    this.disposed = true;
    console.log('[AdapterManager] Disposed');
  }

  /**
   * Reset the manager - alias for dispose()
   * Useful for testing or forced re-initialization
   */
  reset(): void {
    this.dispose();
  }

  /**
   * Get statistics about managed adapters
   */
  getStats(): {
    adapterCount: number;
    connectedCount: number;
    modes: SocketMode[];
  } {
    let connectedCount = 0;
    const modes: SocketMode[] = [];

    for (const [mode, adapter] of this.adapters.entries()) {
      modes.push(mode);
      if (adapter.isConnected()) {
        connectedCount++;
      }
    }

    return {
      adapterCount: this.adapters.size,
      connectedCount,
      modes,
    };
  }
}

// ============================================
// Singleton Instance
// ============================================

/**
 * Global AdapterManager instance
 * Use this for all adapter operations
 */
let adapterManager: AdapterManager = new AdapterManager();

/**
 * Get the global AdapterManager instance
 */
export function getAdapterManager(): AdapterManager {
  return adapterManager;
}

// ============================================
// HMR Support
// ============================================

// Vite HMR cleanup
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    console.log('[AdapterManager] HMR dispose triggered');
    adapterManager.dispose();
  });

  // Accept updates to re-initialize if needed
  import.meta.hot.accept(() => {
    console.log('[AdapterManager] HMR accept - creating new manager');
    adapterManager = new AdapterManager();
  });
}

// ============================================
// Backwards-Compatible Exports
// ============================================

/**
 * @deprecated Use getAdapterManager().getAdapter('self-hosted') instead
 * Get or create the Self-Hosted adapter instance
 */
export function getSelfHostedAdapter(): SelfHostedSocketAdapter {
  return adapterManager.getAdapter('self-hosted') as SelfHostedSocketAdapter;
}

/**
 * Get adapter by mode
 * Delegates to AdapterManager for lifecycle management
 *
 * @param mode - Socket mode (only 'self-hosted' is supported)
 * @returns The appropriate adapter instance
 */
export function getAdapter(mode: SocketMode): ISocketAdapter {
  return adapterManager.getAdapter(mode);
}

/**
 * Get the preferred adapter based on feature flags
 *
 * @returns The preferred adapter instance
 */
export function getPreferredAdapter(): ISocketAdapter {
  const mode = getPreferredMode();
  return adapterManager.getAdapter(mode);
}

/**
 * Create and connect an adapter
 *
 * @param config - Connection configuration (includes mode)
 * @returns Connected adapter or null on failure
 */
export async function createConnectedAdapter(
  config: ConnectionConfig
): Promise<ISocketAdapter | null> {
  const mode = config.mode || getPreferredMode();
  const adapter = adapterManager.getAdapter(mode);

  const success = await adapter.connect(config);
  if (success) {
    return adapter;
  }

  return null;
}

/**
 * @deprecated Use getAdapterManager().dispose() instead
 * Disconnect all adapters
 */
export function disconnectAllAdapters(): void {
  adapterManager.dispose();
}

/**
 * Get the currently connected adapter (if any)
 *
 * @returns Connected adapter or null
 */
export function getConnectedAdapter(): ISocketAdapter | null {
  return adapterManager.getConnectedAdapter();
}

/**
 * Check if any adapter is currently connected
 */
export function isAnyAdapterConnected(): boolean {
  return adapterManager.isAnyConnected();
}

/**
 * Get the mode of the currently connected adapter
 *
 * @returns Connected mode or null
 */
export function getConnectedMode(): SocketMode | null {
  return adapterManager.getConnectedMode();
}

// ============================================
// Settings Utilities (BUG-003 FIX)
// ============================================

/**
 * Settings type for sendSettings function
 */
export interface SettingsPayload {
  permissionMode: 'default' | 'accept-edits' | 'plan-mode' | 'yolo-mode';
  modelOption: 'cli-settings' | 'opus-4-5' | 'sonnet-4-5' | 'haiku-4-5' | 'opus-then-sonnet';
  sessionId?: string;
}

/**
 * Send settings to daemon via the connected adapter
 * FIX for BUG-003: Uses the self-hosted adapter's sendSettings if connected,
 * otherwise falls back to cloud adapter or returns error.
 *
 * @param settings - Settings to send to daemon
 * @returns Promise resolving to success/error result
 */
export async function sendSettings(settings: SettingsPayload): Promise<{ success: boolean; error?: string }> {
  // Get connected adapter via AdapterManager
  const connectedMode = adapterManager.getConnectedMode();

  if (connectedMode === 'self-hosted') {
    const selfHostedAdapter = adapterManager.getAdapter('self-hosted') as SelfHostedSocketAdapter;
    return selfHostedAdapter.sendSettings(settings);
  }

  console.error('[Adapters] Cannot send settings - no adapter connected');
  return { success: false, error: 'Not connected to relay server' };
}

// ============================================
// AI Title Generation Utilities
// ============================================

/**
 * Request AI-powered title generation via the connected adapter
 * Uses daemon's Anthropic SDK with user's Claude Code CLI Max subscription
 *
 * FIX for title generation: Uses socket events instead of REST API,
 * leveraging the user's existing Claude subscription via the daemon.
 *
 * @param message - User message to generate title from
 * @param sessionId - Session ID to associate with the title
 * @returns Promise resolving to generated title or null on failure
 */
export async function requestTitleGeneration(
  message: string,
  sessionId: string
): Promise<{ title: string; aiGenerated: boolean } | null> {
  // Get connected adapter via AdapterManager
  const connectedMode = adapterManager.getConnectedMode();

  if (connectedMode === 'self-hosted') {
    const selfHostedAdapter = adapterManager.getAdapter('self-hosted') as SelfHostedSocketAdapter;
    return selfHostedAdapter.requestTitleGeneration(message, sessionId);
  }

  console.warn('[Adapters] Cannot request title - no self-hosted adapter connected');
  return null;
}

// ============================================
// Testing Utilities
// ============================================

/**
 * Reset all adapter instances (for testing)
 * Delegates to AdapterManager.reset()
 */
export function resetAdapters(): void {
  adapterManager.reset();
  // Re-create manager for fresh state in tests
  adapterManager = new AdapterManager();
}
